import { showCustomToast } from 'common/components/CustomToast/utils';
import { useMutation } from '@apollo/client';
import {
  ContextualMenu,
  IDragOptions,
  Modal,
  Stack,
  makeStyles,
} from '@fluentui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  UserGroupCreateInput,
  UserGroupUpdateInput,
} from 'common/types/globalTypes';
import React, { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { UserGroupProps } from '..';
import { BasicForm } from './BasicForm';
import { Footer } from './Footer';
import { Header } from './Header';
import {
  UserGroupCreate,
  UserGroupCreateVariables,
} from './__generated__/UserGroupCreate';
import {
  UserGroupUpdate,
  UserGroupUpdateVariables,
} from './__generated__/UserGroupUpdate';
import { UserGroupValues } from './types';
import { getDefaultValues } from './utils';
import { validationSchema } from './validation';
import USER_GROUP_CREATE from './UserGroupCreate.graphql';
import USER_GROUP_UPDATE from './UserGroupUpdate.graphql';
import USER_GROUP_FIELDS from '../../list/UserGroupFields.graphql';
const USER_GROUP_FRAGMENT_NAME = 'UserGroupFields';

const DragOptions: IDragOptions = {
  moveMenuItemText: 'Move',
  closeMenuItemText: 'Close',
  menu: ContextualMenu,
  dragHandleSelector: '.ms-Modal-scrollableContent > div > div:first-child ',
};

const useStyles = makeStyles(() => ({
  container: {
    width: 800,
    maxHeight: 600,
  },
}));

type UserGroupModalProps = Partial<UserGroupProps> & {
  onClose: () => void;
};

export const UserGroupModal: React.FC<UserGroupModalProps> = ({
  userGroup,
  onClose,
}) => {
  const styles = useStyles();

  const [createUserGroup, { loading: createUserGroupLoading }] = useMutation<
    UserGroupCreate,
    UserGroupCreateVariables
  >(USER_GROUP_CREATE, { errorPolicy: 'all' });
  const [updateUserGroup, { loading: updateUserGroupLoading }] = useMutation<
    UserGroupUpdate,
    UserGroupUpdateVariables
  >(USER_GROUP_UPDATE, { errorPolicy: 'all' });

  const formMethods = useForm<UserGroupValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema()),
  });
  const { handleSubmit, reset, trigger } = { ...formMethods };
  const { id, _rowTimestamp } = { ...userGroup };
  const isEdit = !!id;
  const triggerCallBack = useCallback(trigger, []);

  useEffect(() => {
    triggerCallBack();
  }, [triggerCallBack]);

  const onHandleSubmit = async (values: UserGroupValues) => {
    const { name, description, isAccessOnlyToCreatedRows } = { ...values };
    if (!!id && !!_rowTimestamp) {
      const { errors } = await updateUserGroup({
        variables: {
          input: {
            id,
            rowTimestamp: _rowTimestamp,
            userGroupPatch: {
              name,
              description,
              isAccessOnlyToCreatedRows,
            },
          } as UserGroupUpdateInput,
        },
      });
      if (errors?.length)
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      else {
        showCustomToast({
          content: 'User groups updated successfully',
          appearance: 'success',
        });
      }
    } else {
      const { errors } = await createUserGroup({
        variables: {
          input: {
            userGroup: {
              name,
              description,
              isAccessOnlyToCreatedRows,
            },
          } as UserGroupCreateInput,
        },
        update: (cache, { data }) => {
          if (!!data?.userGroupCreate?.userGroup?.id) {
            cache.modify({
              fields: {
                userGroups(existing) {
                  if (data?.userGroupCreate?.userGroup) {
                    const newUserGroupRef = cache.writeFragment({
                      data: data.userGroupCreate.userGroup,
                      fragment: USER_GROUP_FIELDS,
                      fragmentName: USER_GROUP_FRAGMENT_NAME,
                    });
                    return {
                      ...existing,
                      nodes: [newUserGroupRef, ...existing.nodes],
                    };
                  }
                },
              },
            });
          }
        },
      });
      if (errors?.length)
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      else {
        showCustomToast({
          content: 'User group created successfully',
          appearance: 'success',
        });
        onClose();
      }
    }
  };
  useEffect(() => {
    if (userGroup) {
      const defaultValues = getDefaultValues({
        userGroup,
      });
      reset(defaultValues);
    }
    trigger();
  }, [userGroup, reset]);

  return (
    <Modal isOpen isBlocking dragOptions={DragOptions}>
      <Stack className={styles.container}>
        <FormProvider {...formMethods}>
          <Header
            onClose={onClose}
            title={userGroup ? 'Update User Group' : 'Create User Group'}
            isEdit={isEdit}
          />
          <BasicForm />
          <Footer
            loading={updateUserGroupLoading || createUserGroupLoading}
            onSubmit={handleSubmit(onHandleSubmit)}
            onCancel={onClose}
          />
        </FormProvider>
      </Stack>
    </Modal>
  );
};
